<!--
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
SPDX-License-Identifier: Apache-2.0
-->

<!-- This SFC provides a form for creating or editing tenants -->

<template>
  <!-- 🔵 Intent: Task 006 - icons + tooltips + Japanese form-text descriptions. -->
  <div class="d-flex flex-column mb-2 p-4 border border-2 rounded">
    <p class="lead mb-3">
      <i
        :class="['bi', isEditMode ? 'bi-pencil-square' : 'bi-plus-square']"
        class="me-2 text-primary"
        aria-hidden="true"
      ></i>
      {{ isEditMode ? 'テナントを編集' : 'テナントを作成' }}
    </p>

    <!-- error message -->
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- success message -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <form @submit.prevent="onSubmit" novalidate>
      <!-- tenant name -->
      <div class="mb-3">
        <label class="form-label h6">
          <i class="bi bi-tag-fill me-1" aria-hidden="true"></i>
          Name
        </label>
        <input
          type="text"
          class="form-control"
          placeholder="Tenant name (required)"
          v-model="form.name"
          :class="{ 'is-invalid': submitted && !form.name }"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="管理画面に表示される、人が読むためのテナント名。"
        />
        <div class="invalid-feedback">Name is required.</div>
        <div class="form-text">管理画面の一覧などに表示されるテナント表示名（必須）。</div>
      </div>

      <!-- event ID (only for create mode) -->
      <div class="mb-3">
        <label class="form-label h6">
          <i class="bi bi-hash me-1" aria-hidden="true"></i>
          Event ID
        </label>
        <input
          type="text"
          class="form-control"
          placeholder="Unique event identifier (required)"
          v-model="form.event_id"
          :disabled="isEditMode"
          :class="{ 'is-invalid': submitted && !form.event_id }"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="テナントを一意に識別する英数字 ID。待機室 URL のサブドメインに使われます。"
        />
        <div class="invalid-feedback">Event ID is required.</div>
        <div class="form-text">
          テナントを識別する一意な ID（必須）。
          待機室 URL <code>{event_id}.vwr.&lt;domain&gt;</code> のサブドメインとして利用されます。
          <span v-if="isEditMode">作成後は変更できません。</span>
        </div>
      </div>

      <!-- inlet strategy -->
      <div class="mb-3">
        <label class="form-label h6">
          <i class="bi bi-funnel-fill me-1" aria-hidden="true"></i>
          Inlet Strategy
        </label>
        <select
          class="form-select"
          v-model="form.inlet_strategy"
          :class="{ 'is-invalid': submitted && !form.inlet_strategy }"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="待機室から本サイトへ通すユーザの選び方を切り替えます。"
        >
          <option value="" disabled>Select a strategy</option>
          <option value="MaxSize">MaxSize</option>
          <option value="Periodic">Periodic</option>
        </select>
        <div class="invalid-feedback">Inlet strategy is required.</div>
        <div class="form-text">
          MaxSize: 同時にターゲットサイトに居られる人数の上限を保つように制御。<br />
          Periodic: 一定間隔ごとに ServingCounter を進めて段階的に通します。
        </div>
      </div>

      <!-- max_size field (shown for max_size strategy) -->
      <div v-if="form.inlet_strategy === 'MaxSize'" class="mb-3">
        <label class="form-label h6">
          <i class="bi bi-people-fill me-1" aria-hidden="true"></i>
          Max Size
        </label>
        <input
          type="number"
          class="form-control"
          placeholder="Maximum concurrent users"
          v-model.number="form.max_size"
          min="1"
          :class="{ 'is-invalid': submitted && form.inlet_strategy === 'MaxSize' && !isValidMaxSize }"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="同時にターゲットサイトを利用できる最大ユーザ数。"
        />
        <div class="invalid-feedback">Max size must be a positive number.</div>
        <div class="form-text">
          MaxSize Inlet Strategy のときに有効。
          ターゲットサイトに同時に存在できる最大ユーザ数を指定します（1 以上）。
        </div>
      </div>

      <!-- increment_by field (shown for periodic strategy) -->
      <div v-if="form.inlet_strategy === 'Periodic'" class="mb-3">
        <label class="form-label h6">
          <i class="bi bi-stopwatch-fill me-1" aria-hidden="true"></i>
          Increment By
        </label>
        <input
          type="number"
          class="form-control"
          placeholder="Serving counter increment amount"
          v-model.number="form.increment_by"
          min="1"
          :class="{ 'is-invalid': submitted && form.inlet_strategy === 'Periodic' && !isValidIncrementBy }"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Periodic 戦略で 1 回ごとに ServingCounter を進める数。"
        />
        <div class="invalid-feedback">Increment by must be a positive number.</div>
        <div class="form-text">
          Periodic Inlet Strategy のときに有効。
          スケジュール起動 1 回あたり ServingCounter を進める数を指定します（1 以上）。
        </div>
      </div>

      <!-- action buttons -->
      <div class="d-flex flex-row-reverse">
        <button
          type="button"
          class="btn btn-outline-secondary rounded m-1"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="編集中の内容を破棄してフォームをクリアします。"
          @click="onCancel"
        >
          <i class="bi bi-x-lg me-1" aria-hidden="true"></i>
          キャンセル
        </button>
        <button
          type="submit"
          class="btn btn-primary rounded m-1"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="isEditMode ? 'テナント設定を更新します。' : '新しいテナントを作成します。'"
          :disabled="saving"
        >
          <i
            :class="['bi', isEditMode ? 'bi-save' : 'bi-plus-lg', 'me-1']"
            aria-hidden="true"
          ></i>
          {{ saving ? '保存中...' : (isEditMode ? '更新' : '作成') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useTenantStore } from "../stores/tenant";
import { useBootstrapTooltips } from "../composables/useBootstrapTooltips";

// 🔵 Intent: Task 006 - bootstrap tooltips when this component mounts.
useBootstrapTooltips();

const props = defineProps({
  tenant: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: "create",
    validator: (value) => ["create", "edit"].includes(value),
  },
});

const emit = defineEmits(["saved", "cancelled"]);

const tenantStore = useTenantStore();

function initForm() {
  if (props.tenant) {
    return {
      name: props.tenant.name || "",
      event_id: props.tenant.event_id || "",
      inlet_strategy: props.tenant.inlet_strategy || "",
      max_size: props.tenant.max_size || null,
      increment_by: props.tenant.increment_by || null,
    };
  }
  return {
    name: "",
    event_id: "",
    inlet_strategy: "",
    max_size: null,
    increment_by: null,
  };
}

const form = reactive(initForm());
const submitted = ref(false);
const saving = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const isEditMode = computed(() => props.mode === "edit");

const isValidMaxSize = computed(() =>
  typeof form.max_size === "number" && form.max_size > 0
);

const isValidIncrementBy = computed(() =>
  typeof form.increment_by === "number" && form.increment_by > 0
);

const isFormValid = computed(() => {
  if (!form.name || !form.event_id || !form.inlet_strategy) {
    return false;
  }
  if (form.inlet_strategy === "MaxSize" && !isValidMaxSize.value) {
    return false;
  }
  if (form.inlet_strategy === "Periodic" && !isValidIncrementBy.value) {
    return false;
  }
  return true;
});

watch(
  () => props.tenant,
  () => {
    Object.assign(form, initForm());
    submitted.value = false;
    errorMessage.value = "";
    successMessage.value = "";
  },
  { deep: true }
);

function buildPayload() {
  const payload = {
    name: form.name,
    event_id: form.event_id,
    inlet_strategy: form.inlet_strategy,
  };
  if (form.inlet_strategy === "MaxSize") {
    payload.max_size = form.max_size;
  }
  if (form.inlet_strategy === "Periodic") {
    payload.increment_by = form.increment_by;
  }
  return payload;
}

async function onSubmit() {
  submitted.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  if (!isFormValid.value) {
    return;
  }

  saving.value = true;
  try {
    const payload = buildPayload();

    if (isEditMode.value) {
      await tenantStore.updateTenant(form.event_id, payload);
      successMessage.value = "Tenant updated successfully.";
    } else {
      await tenantStore.createTenant(payload);
      successMessage.value = "Tenant created successfully.";
      // reset form after successful creation
      Object.assign(form, initForm());
      submitted.value = false;
    }

    emit("saved", payload);
  } catch (error) {
    errorMessage.value = error.message || "Failed to save tenant.";
  } finally {
    saving.value = false;
  }
}

function onCancel() {
  Object.assign(form, initForm());
  submitted.value = false;
  errorMessage.value = "";
  successMessage.value = "";
  emit("cancelled");
}
</script>
